import React, { useCallback } from 'react'

import NameTripModalView from './Templates/NameTripModalView'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import FirstTime from 'tripPlanner/components/EntryPoints/Common/Copy'
import TripSelect from 'tripPlanner/components/EntryPoints/ModalChildren/TripSelect'
import { useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import { useEditableTrips } from 'tripPlanner/hooks/api/trip'
import { useProcessBookmarks } from 'tripPlanner/hooks/api/bookmark'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { BasicTrip, FullTrip } from 'tripPlanner/types/common'
import { PublicFullTrip } from 'tripPlanner/types/publicTrip'
import { TripItem } from 'tripPlanner/types/tripItem'
import { isLeBookmark } from 'tripPlanner/utils'
import { itemToPublicTripsBookmarkPayload } from 'tripPlanner/utils/publicTrips/itemToPublicTripBookmarkPayload'
import { itemToPublicTripItemPayload } from 'tripPlanner/utils/publicTrips/itemToPublicTripItemPayload'
interface Props {
  item: TripItem
  trip: FullTrip | PublicFullTrip
}

export default function SelectTripModalView({ item, trip }: Props) {
  const { closeModal, goBack } = useModalCallbacks()
  const { data: trips } = useEditableTrips()
  const openModal = useOpenModal()

  const { showSaveErrorSnackbar, showSaveSuccessSnackbar } =
    useBookmarkSnackbarHandlers()

  const { mutate: processBookmarks, isLoading: isProcessingBookmarks } =
    useProcessBookmarks({
      onSuccess: (res, _, trip) => {
        if (trip) {
          showSaveSuccessSnackbar(trip.id, trip.name, res.created[0].id)
        }
        closeModal()
      },
      onError: () => showSaveErrorSnackbar(trip.name),
    })

  const { mutate: createCustomItem, isLoading: isCreatingItems } =
    useCreateTripItem({
      onSuccess: (item, _, trip) => {
        if (trip) {
          showSaveSuccessSnackbar(item.tripId, trip.name, item.id)
        }
        closeModal()
      },
      onError: () => showSaveErrorSnackbar(trip.name),
    })

  const saveItemToTrip = useCallback(
    (trip: BasicTrip) => {
      if (isLeBookmark(item)) {
        processBookmarks({
          items: [itemToPublicTripsBookmarkPayload(item)],
          tripIdsAdded: [trip.id],
          tripIdsRemoved: [],
        })
      } else {
        createCustomItem({
          tripId: trip.id,
          tripItem: itemToPublicTripItemPayload(item),
        })
      }
    },
    [item, processBookmarks, createCustomItem],
  )

  const openTripNameView = useCallback(
    () => openModal(NameTripModalView, { trip, item }),
    [item, trip, openModal],
  )

  const isLoading = isCreatingItems || isProcessingBookmarks

  return (
    <>
      <ModalHeader
        title="Select a trip"
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <BodyText variant="large">
              Saving this order does not hold the booking. Price and
              availability are subject to change.
            </BodyText>
            {trips && trips.length === 0 && <FirstTime />}
            {trips && (
              <div>
                <TripSelect
                  onCreateSelect={openTripNameView}
                  onSelectTrip={saveItemToTrip}
                  trips={trips}
                />
              </div>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}
